import React, { useEffect, useState } from "react";
import { auth, db } from "../firebase/firebase-config";
import { doc, getDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css'; // Import Toastify CSS

// Generate a random crime-case number for display
const generateCaseNumber = () => {
    return `Crime-Case No-${Math.floor(100000 + Math.random() * 900000)}`; // Generates a 6-digit case number, e.g., CASE-123456
};

const Accountability = () => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isOpen, setIsOpen] = useState(false);
    const [pin, setPin] = useState("");
    const [safetyPin, setSafetyPin] = useState("");
    const navigate = useNavigate();

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (currentUser) {
                const userRef = doc(db, "users", currentUser.uid);
                const userSnap = await getDoc(userRef);

                if (userSnap.exists()) {
                    const userData = userSnap.data();
                    setUser(userData);
                    setSafetyPin(userData.safetyPin || "");
                }
            } else {
                setUser(null);
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const handlePinChange = (e) => {
        setPin(e.target.value);
    };

    const handleClose = () => {
        setIsOpen(false);
        setPin("");
    };

    const handleSubmit = () => {
        if (pin.length === 4) {
            if (pin === safetyPin) {
                toast.success("PIN matched successfully! Redirecting to Legal Review."); // Success Toast
                setTimeout(() => {
                    navigate("/legal"); // Redirect after success
                }, 2000); // Wait 2 seconds before navigating
            } else {
                toast.error("Incorrect PIN. Please try again."); // Error Toast
            }
        } else {
            toast.error("Please enter a 4-digit PIN"); // Error Toast for invalid length
        }
    };

    if (loading) {
        return <p className="text-center text-white">Loading user data...</p>;
    }

    // Generate crime-case number for this instance
    const caseNumber = generateCaseNumber();

    return (
        <>
        <div className="flex justify-center items-center min-h-screen bg-[#020824] p-6">
            <div className="w-full max-w-2xl bg-white p-8 shadow-lg rounded-lg">
                <h2 className="text-2xl font-semibold text-center text-gray-800 mb-4">
                    Preliminary Accountability Review (PAR)
                </h2>
                <p className="text-gray-600 mb-6 text-sm text-center">
                    Request for Clarification
                </p>

                {user ? (
                    <>
                        <p className="text-gray-800 mb-4">
                            Dear <strong>{user.firstName} {user.lastName}</strong>,
                        </p>
                        <p className="text-gray-800 mb-4">
                            We are conducting a Preliminary Accountability Review regarding recent activities of {caseNumber} linked to your account. Please respond to the following:
                        </p>

                        <ul className="list-disc list-inside text-gray-800 mb-4">
                            <li>Were you aware of this activity?</li>
                            <li>Can you provide any supporting details or documents?</li>
                            <li>Did anyone else have access to your account during this period?</li>
                        </ul>

                        <p className="text-gray-800 mb-4">
                            Your response is crucial for accuracy. Afterward, the matter will be forwarded to the legal department for further review.
                        </p>

                        <p className="text-gray-800 mb-4">
                            Please submit your response no later than <strong>24 hours</strong>. Failure to do so may result in escalated action.
                        </p>

                        <p className="text-gray-800 mb-4">
                            For questions, contact us at <strong>+16232135858, +15204015463</strong> or reach out to Jay Grossman, Crime Investigator, at <a href="mailto:ciajaygrossman@gmail.com" className="text-blue-600 hover:text-blue-800">ciajaygrossman@gmail.com</a>.
                        </p>
                    </>
                ) : (
                    <p className="text-center text-gray-600">No user signed in.</p>
                )}

                <div className="mt-4">
                    <button onClick={() => setIsOpen(true)} className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500">
                        Proceed to Legal Review
                    </button>

                    {isOpen && (
                        <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex justify-center items-center z-50">
                            <div className="bg-white rounded-lg p-6 w-80 sm:w-96">
                                <div className="flex justify-between items-center">
                                    <h2 className="text-lg font-semibold text-gray-800">Enter 4-Digit Pin</h2>
                                    <button onClick={handleClose} className="text-gray-600 hover:text-gray-800">
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-6 h-6">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                                <input
                                    type="text"
                                    value={pin}
                                    onChange={handlePinChange}
                                    maxLength={4}
                                    className="w-full text-center py-2 mt-4 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Enter PIN"
                                />
                                <div className="mt-4 flex justify-between">
                                    <button onClick={handleClose} className="bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700">
                                        Cancel
                                    </button>
                                    <button onClick={handleSubmit} className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700">
                                        Submit
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>

        {/* Toast Container for displaying toasts */}
        <ToastContainer 
            position="top-center" 
            autoClose={5000} 
            hideProgressBar={false} 
            newestOnTop={false} 
            closeOnClick 
            rtl={false} 
            pauseOnFocusLoss 
            draggable 
            pauseOnHover 
        />
        </>
    );
};

export default Accountability;